import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@mui/styles";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import SaveIcon from "@mui/icons-material/SaveAlt";

import ISXUtils from "services/Utils";
import AlertItem from "components/AlertItem";

const uuidv4 = require("uuid/v4");

const useStyles = makeStyles((theme) => ({
  alertsActionsHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  alertItemsContainer: {
    height: "100%",
    paddingLeft: 4,
    paddingRight: 4,
    overflow: "auto",
    borderTop: "1px dotted lightgray",
  },
  actionButton: {
    marginLeft: 3,
    marginRight: 3,
  },
}));

const StackAlerts = ({
  stack,
  updateItem,
  setTitle,
  readOnly,
  dirty,
  setDirty,
}) => {
  const [alerts, setAlerts] = useState([]);
  const [removed, setRemoved] = useState(new Set());
  const [toastOpen, setToastOpen] = useState(false);
  const lastStackId = useRef(null);

  const classes = useStyles();

  useEffect(() => {
    setTitle("Alerts Configuration");
  }, [setTitle]);

  useEffect(() => {
    if (stack && stack.guuid !== lastStackId.current) {
      lastStackId.current = stack.guuid;
      const alerts = ((stack.data_config || {}).dsitems || [])
        .map((tagName) => (stack.data_config[tagName] || {}).alarms || [])
        .filter(Boolean)
        .flat();
      setAlerts(alerts);
    }
  }, [stack]);

  const saveThisAlert = (guuid, alert) => {
    if (guuid === stack.guuid) {
      // the GUUIDs should always match in this use case
      const idx = alerts.findIndex((a) => a.guuid === alert.guuid);
      if (idx !== -1) {
        const updatedAlerts = [...alerts];
        updatedAlerts[idx] = alert;
        setAlerts(updatedAlerts);
        setDirty(true);
      }
    }
  };

  const reset = () => {
    const alerts = ((stack.data_config || {}).dsitems || [])
      .map((tagName) => (stack.data_config[tagName] || {}).alarms || [])
      .filter(Boolean)
      .flat();
    setAlerts(alerts);
    setDirty(false);
  };

  const createAlert = () => {
    // default alert properties
    const newAlert = {
      guuid: uuidv4(),
      stackId: stack.guuid,
      almszd: 600,
      cdn: [1, 0],
      enabled: false,
      sendto: [],
      schedule: {
        period: "none",
        start: 0,
        end: 0,
      },
      tagName: "",
      derivedtag: "sample",
    };
    setAlerts([...alerts, newAlert]);
    setDirty(true);
  };

  const deleteThisAlert = (guuid, alarmGuuidToDelete) => {
    if (guuid === stack.guuid) {
      const removedKey = `data_config.${ISXUtils.escapeTagPeriods(
        alerts.filter((a) => a.guuid === alarmGuuidToDelete)[0].tagName
      )}.alarms`;
      const newAlerts = alerts.filter((a) => a.guuid !== alarmGuuidToDelete);
      setAlerts(newAlerts);
      setRemoved((prev) => new Set(prev.add(removedKey)));
      setDirty(true);
    }
  };

  const closeToast = () => {
    setToastOpen(false);
  };

  async function saveAlertsToDB() {
    let alertsByTag = {};
    alertsByTag = alerts.reduce((acc, a) => {
      if (a.tagName) {
        const tagNameEscaped = ISXUtils.escapeTagPeriods(a.tagName);
        const key = `data_config.${tagNameEscaped}.alarms`;
        let tagAlerts = acc[key];
        if (!tagAlerts) {
          tagAlerts = acc[key] = [];
        }
        tagAlerts.push(a);
      }
      return acc;
    }, alertsByTag);
    for (let removeKey of removed) {
      if (!(removeKey in alertsByTag)) {
        alertsByTag[removeKey] = [];
      }
    }
    setDirty(false);
    await updateItem(stack.guuid, alertsByTag);
    setToastOpen(true);
  }

  if (stack) {
    return (
      <>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <div className={classes.alertsActionsHeader}>
              <div>
                <Typography
                  variant="overline"
                  className={classes.actionbutton}
                >{`${alerts.length} configured`}</Typography>
                {!readOnly && (
                  <Button
                    className={classes.actionbutton}
                    color="primary"
                    onClick={createAlert}
                    startIcon={<AddCircleIcon />}
                  >
                    Create New
                  </Button>
                )}
              </div>
              {dirty && (
                <div>
                  <Button
                    className={classes.actionbutton}
                    disabled={toastOpen}
                    color="primary"
                    onClick={saveAlertsToDB}
                    startIcon={<SaveIcon />}
                  >
                    Save
                  </Button>
                  <Tooltip
                    title="Current changes will be discarded"
                    placement="top"
                  >
                    <span>
                      <Button
                        className={classes.actionbutton}
                        disabled={toastOpen}
                        style={{ color: "red" }}
                        onClick={reset}
                      >
                        Cancel
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
          </Grid>
        </Grid>
        <div className={classes.alertItemsContainer}>
          <Grid container>
            {alerts.length > 0 ? (
              alerts.map((alert, idx) => (
                <div key={idx} style={{ width: "100%" }}>
                  <AlertItem
                    deleteThisAlarm={deleteThisAlert}
                    saveThisAlarm={saveThisAlert}
                    stacks={[stack]}
                    thisStack={stack}
                    stackId={stack.guuid}
                    alarm={alert}
                    readOnly={readOnly}
                  />
                </div>
              ))
            ) : (
              <div></div>
            )}
          </Grid>
          <Snackbar
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            open={toastOpen}
            autoHideDuration={3000}
            onClose={closeToast}
            message="Alerts saved"
          />
        </div>
      </>
    );
  } else {
    return null;
  }
};

export default StackAlerts;
